import axios from "axios";

import { apiKey, unixSeconds, secureHashFor } from "@util/authInfo";
import { captureUnexpectedClientError } from "@util/sentry";
import { toErrorMessage } from "@util/toErrorMessage";

axios.defaults.baseURL = "/api";

axios.interceptors.request.use(
  (request) => {
    const timestamp = unixSeconds();
    request.headers["api-key"] = apiKey;
    request.headers["timestamp"] = timestamp;
    request.headers["secure-hash"] = secureHashFor(timestamp);

    if (
      requestWhitelist.includes(request.url) ||
      request.url.indexOf("/code/") > -1 ||
      request.url.indexOf("/verify-code/") > -1 ||
      request.url.indexOf("/set-session/") > -1 ||
      request.url.indexOf("microsite") > -1 ||
      request.url.indexOf("trouble-tickets") > -1 ||
      request.url.indexOf("/forgot-password") > -1
    ) {
      request.headers["internal"] = true;
    }

    return request;
  },
  (error) => {
    return Promise.reject(
      toErrorMessage(
        error,
        "Our engineers are currently performing maintenance on the site. Sorry for any inconvenience."
      )
    );
  }
);

axios.interceptors.response.use(
  (response) => {
    if (response.config.responseType === "arraybuffer") {
      const contentType = String(response.headers["content-type"] || "");
      if (!contentType.includes("application/pdf")) {
        const text = new TextDecoder().decode(response.data);
        try {
          const parsed = JSON.parse(text);
          if (parsed.response_code !== undefined && parsed.response_code !== 0) {
            return Promise.reject(
              toErrorMessage(parsed.response_message, "Request failed")
            );
          }
        } catch (exception) {
          return Promise.reject(toErrorMessage(text, "PDF download failed"));
        }
      }

      return response;
    }

    // * if response isn't set up with response codes
    if (response.data.response_code === undefined) {
      return response;
    }

    // return to login
    if (
      response.data.response_code === 2 &&
      response.data.response_message &&
      response.data.response_message === "Back to Login"
    ) {
      const requestUrl = String(response.config?.url || "");
      const printTicketHeader = response.config?.headers?.["print-ticket"];
      const pagePrintTicket =
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("print_ticket");
      if (
        requestUrl.includes("print_ticket=") ||
        printTicketHeader ||
        pagePrintTicket
      ) {
        return Promise.reject(
          toErrorMessage(response.data.response_message, "Request failed")
        );
      }

      if (localStorage.getItem("clearscreen-entry-point")) {
        return (window.location = localStorage.getItem(
          "clearscreen-entry-point"
        ));
      }

      return (window.location = `/`);
    }

    // modal
    if (response.data.response_code === 3) {
      return response;
    }

    // Business outcomes (permission, validation, etc.) — do not send to Sentry.
    if (response.data.response_code !== 0) {
      if (response.data.response_code === 2 && response.data.response) {
        // return response;
        return response;
      }
      // Prefer reject over throw so callers always get a string, not an Error object.
      return Promise.reject(
        toErrorMessage(response.data.response_message, "Request failed")
      );
    }

    return response;
  },
  (error) => {
    // Already normalized to a string by a prior interceptor / business reject.
    if (typeof error === "string") {
      return Promise.reject(error);
    }

    captureUnexpectedClientError(error, { source: "axios_interceptor" });

    return Promise.reject(
      toErrorMessage(
        error,
        "Our engineers are currently performing maintenance on the site. Sorry for any inconvenience."
      )
    );
  }
);

export default axios;

const requestWhitelist = [
  `/admin-login`,
  `/admin-passkey-login/options`,
  `/admin-passkey-login/verify`,
  `/forgot-password`,
  `/user-invite-decision`,
  `/user-login`,
  `/user-passkey-login/options`,
  `/user-passkey-login/verify`,
  `/passkey-mfa/options`,
  `/passkey-mfa/verify`,
  `/members/complete-signup`,
  `/members/resend-signup-email-link`,
  `/members/signup`,
  `/password`,
  `/resend-signup`,
  `/send-otp-code`,
  `/status`,
  `/user-login`,
  `/verifications/phone/check`,
  `/verifications/phone`,
  `/verify-text-code`,
].map((w) => `${w}/`);
